/* stylelint-disable max-nesting-depth */

/**
    @prop --uic-checkbox-border-color: the default border color
    @prop --uic-checkbox-border-color-checked: the border color in checked state
    @prop --uic-checkbox-border-color-focus: the border color in focus state
    @prop --uic-checkbox-border-color-hover: the border color in hover state
    @prop --uic-checkbox-border-color-active: the border color in active state
    @prop --uic-checkbox-border-color-disabled: the border color in disabled state

    @prop --uic-checkbox-background-color: the default background
    @prop --uic-checkbox-background-color-focus: the background in focus state
    @prop --uic-checkbox-background-color-hover: the background in hover state
    @prop --uic-checkbox-background-color-active: the background in active state
    @prop --uic-checkbox-background-color-disabled: the background in disabled state

    @prop --uic-checkbox-mark-color: the default checkmark color
    @prop --uic-checkbox-mark-color-focus: the checkmark color in focus state
    @prop --uic-checkbox-mark-color-hover: the checkmark color in hover state
    @prop --uic-checkbox-mark-color-active: the checkmark color in active state
    @prop --uic-checkbox-mark-color-disabled: the checkmark color in disabled state

    @prop --uic-checkbox-text-color: the default text color
    @prop --uic-checkbox-text-color-focus: the text color in focus state
    @prop --uic-checkbox-text-color-hover: the text color in hover state
    @prop --uic-checkbox-text-color-active: the text color in active state
    @prop --uic-checkbox-text-color-disabled: the text color in disabled state

    @prop --uic-checkbox-box-width: the width of the box containing the checkmark
    @prop --uic-checkbox-box-height: the height of the box containing the checkmark

    @prop --uic-checkbox-mark-width: the width of the checkmark
    @prop --uic-checkbox-mark-height: the height of the checkmark
*/

uic-checkbox {
  box-sizing: border-box;
  display: inline-block;

  &[checked] .uic-checkbox__mark::before,
  &[mixed] .uic-checkbox__mark::before {
    border-color: var(--uic-checkbox-border-color-checked);
  }

  &[checked] .uic-checkbox__mark-check {
    opacity: 1;
  }

  &[disabled] {
    pointer-events: none;
  }

  &[mixed] {
    .uic-checkbox__mark-check {
      display: none;
    }

    .uic-checkbox__mark-mixed {
      display: block;
    }
  }

  &[multiline] {
    .uic-checkbox__label {
      white-space: normal;
    }
  }
}

.uic-checkbox {
  &__wrapper {
    align-items: center;
    box-sizing: border-box;
    cursor: pointer;
    display: flex;
    padding: 10px 8px;
    position: relative;
    user-select: none;
    width: 100%;
  }

  &__input {
    margin: 0;
    opacity: 0.0001;
    padding: 0;
    position: absolute;

    &:disabled {
      ~ .uic-checkbox__label {
        color: var(--uic-checkbox-text-color-disabled);
      }

      ~ .uic-checkbox__mark {
        color: var(--uic-checkbox-mark-color-disabled);
      }

      ~ .uic-checkbox__mark::before {
        background-color: var(--uic-checkbox-background-color-disabled);
        border-color: var(--uic-checkbox-border-color-disabled);
      }
    }

    &:focus {
      ~ .uic-checkbox__label {
        color: var(--uic-checkbox-text-color-focus);
      }

      ~ .uic-checkbox__mark {
        color: var(--uic-checkbox-mark-color-focus);
      }

      ~ .uic-checkbox__mark::before {
        background-color: var(--uic-checkbox-background-color-focus);
        outline: 2px solid var(--uic-checkbox-border-color-focus);
        outline-offset: -2px;
      }
    }

    &:hover {
      ~ .uic-checkbox__label {
        color: var(--uic-checkbox-text-color-hover);
      }

      ~ .uic-checkbox__mark {
        color: var(--uic-checkbox-mark-color-hover);
      }

      ~ .uic-checkbox__mark::before {
        background-color: var(--uic-checkbox-background-color-hover);
        border-color: var(--uic-checkbox-border-color-hover);
      }
    }

    &:active {
      ~ .uic-checkbox__label {
        color: var(--uic-checkbox-text-color-active);
      }

      ~ .uic-checkbox__mark {
        color: var(--uic-checkbox-mark-color-active);
      }

      ~ .uic-checkbox__mark::before {
        background-color: var(--uic-checkbox-background-color-active);
        border-color: var(--uic-checkbox-border-color-active);
      }
    }
  }

  &__mark {
    align-items: center;
    /* white background required for alpha-based color scheme */
    background-color: var(--uic-color-white);
    color: var(--uic-checkbox-mark-color);
    display: flex;
    height: var(--uic-checkbox-box-height);
    justify-content: center;
    min-width: var(--uic-checkbox-box-width);
    position: relative;

    width: var(--uic-checkbox-box-width);

    &::before {
      @include absolute();
      background-color: var(--uic-checkbox-background-color);
      border: 1px solid var(--uic-checkbox-border-color);
      content: '';
    }
  }

  &__mark-check,
  &__mark-mixed {
    display: block;
    height: var(--uic-checkbox-mark-height);
    position: relative;

    width: var(--uic-checkbox-mark-width);
  }

  &__mark-check {
    opacity: 0;
  }

  &__mark-mixed {
    display: none;
  }

  &__label {
    @include font(normal);
    @include truncate();

    &:not(:empty) {
      margin-left: 12px;
    }
  }
}

/* stylelint-enable max-nesting-depth */
